import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { analysisApi } from '../api/analysisApi'
import type { SortKey } from '../api/analysisApi'
import { useToast } from '@/hooks/use-toast'

export function useFlaggedArtworks(params: {
  page?: number
  limit?: number
  riskLevel?: string
  adminStatus?: string
  search?: string
  sortBy?: SortKey
  sortDir?: 'asc' | 'desc'
}) {
  return useQuery({
    queryKey: ['analysis', 'flagged', params],
    // Keeps the current page on screen while a new filter/search loads, instead
    // of flashing the empty state between every keystroke.
    placeholderData: (previous) => previous,
    queryFn: () => analysisApi.getFlagged(params),
  })
}

export function useAnalysisDetails(analysisId: number | null) {
  return useQuery({
    queryKey: ['analysis', 'details', analysisId],
    queryFn: () => analysisApi.getDetails(analysisId as number),
    // Only fetched when the evidence dialog actually opens — the row carries
    // the raw Vision payload, which is far larger than the list needs.
    enabled: analysisId !== null,
  })
}

export function usePublishReadiness() {
  return useQuery({
    queryKey: ['analysis', 'readiness'],
    queryFn: () => analysisApi.getReadiness(),
    // Heavier than the others (it touches the products view), so it is fetched
    // on its own rather than blocking the list, and refreshed less often.
    refetchInterval: 120_000,
  })
}

export function usePublishPipeline() {
  return useQuery({
    queryKey: ['analysis', 'pipeline'],
    queryFn: () => analysisApi.getPipeline(),
    refetchInterval: 60_000,
  })
}

export function useAnalysisStats() {
  return useQuery({
    queryKey: ['analysis', 'stats'],
    queryFn: () => analysisApi.getStats(),
    // The analysis queue drains in the background; without this the KPIs sit
    // stale while the operator watches them.
    refetchInterval: 30_000,
  })
}

export function useApproveAnalysis() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ analysisId, notes }: { analysisId: number; notes?: string }) =>
      analysisApi.approve(analysisId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysis', 'flagged'] })
      toast({ title: 'Cleared', description: 'Copyright concern marked resolved' })
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to approve', variant: 'destructive' })
    },
  })
}

export function useRejectAnalysis() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ analysisId, notes }: { analysisId: number; notes: string }) =>
      analysisApi.reject(analysisId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysis', 'flagged'] })
      toast({ title: 'Rejected', description: 'Artwork held for copyright concerns' })
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to reject', variant: 'destructive' })
    },
  })
}
